import { useEffect, useId, useRef, useState } from "react";
import { DxCard } from "./dx";
import { Segmented } from "./dashboard";
import { AlertTriangle, ArrowRight } from "./icons";
import { duration, num } from "../lib/format";
import { isIdle, moneyText } from "../lib/dx";
import { GEO_ZOOM, shapeOf, type MapGeo, type MapMode, type Shape } from "../lib/map-geo";
import type { StatsBreakdownRow } from "../lib/api";

/**
 * ແຜນທີ່ສະຖິຕິ (SDS-Admin-Mockup `dash_map.js`) — สีของพื้นที่ตามตัวชี้วัดที่เลือก
 *
 * - mode `country`: ทุกแขวง (รวมจากขอบเขตเมืองจริง)
 * - mode `province`: ทุกเมืองของแขวงที่กำลังดู (ขอบเขตจริง) — นครหลวงฯ มีเส้นแบ่งตาแสงโดยประมาณซ้อน
 * - mode `district`: ตาแสงของเมืองที่กำลังดู (โดยประมาณ มีเฉพาะนครหลวงฯ) — เมืองอื่นเป็นพื้นหลังจาง
 *
 * ตัวเลขทุกตัวมาจาก /stats/breakdown ชุดเดียวกับตาราง/การ์ดบนจอ ไม่คำนวณใหม่
 * พื้นที่ที่ยังไม่มีเจ้าหน้าที่ประจำและไม่มีคำร้อง = ลายขีด "ຍັງບໍ່ເປີດໃຊ້" ไม่ใช่ 0
 */

type BaseType = "plain" | "osm" | "sat";

const TILES: Record<Exclude<BaseType, "plain">, { url: string; attr: string; max: number }> = {
  osm: { url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", attr: "© OpenStreetMap contributors", max: 17 },
  sat: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attr: "Esri, Maxar, Earthstar Geographics",
    max: 17,
  },
};

const BASE_KEY = "sdds_dash_map_base";

function loadBase(): BaseType {
  try {
    const v = localStorage.getItem(BASE_KEY);
    return v === "plain" || v === "osm" || v === "sat" ? v : "osm";
  } catch {
    return "osm";
  }
}

interface Metric {
  id: string;
  label: string;
  /** null = ไม่มีฐานให้คำนวณ (เช่น ไม่มีคำร้องที่ค้างอยู่) */
  value: (r: StatsBreakdownRow) => number | null;
  text: (v: number) => string;
  note: string;
  disabled?: boolean;
}

const pct = (v: number) => `${v.toLocaleString("lo-LA", { maximumFractionDigits: 1 })}%`;

function metrics(today: boolean, finance: boolean): Metric[] {
  const all: Metric[] = [
    {
      id: "requests",
      label: today ? "ຄຳຮ້ອງໃໝ່" : "ຈຳນວນຄຳຮ້ອງ",
      value: (r) => r.requests,
      text: num,
      note: "ຈຳນວນຄຳຮ້ອງທີ່ຍື່ນ ໃນຊ່ວງເວລາທີ່ເລືອກ",
    },
    {
      id: "issued",
      label: "ອອກເອກະສານແລ້ວ",
      value: (r) => r.issued,
      text: num,
      note: "ຄຳຮ້ອງທີ່ຍື່ນໃນຊ່ວງນີ້ ແລະ ອອກເອກະສານແລ້ວ",
    },
    {
      id: "overdue",
      label: "ຄ້າງເກີນກຳນົດ",
      value: (r) => (r.in_flight > 0 ? (r.overdue_over_7d / r.in_flight) * 100 : null),
      text: pct,
      note: "ສັດສ່ວນຂອງລາຍການທີ່ກຳລັງດຳເນີນ ທີ່ຄ້າງເກີນ 7 ມື້ · ສະຖານະຕອນນີ້",
    },
    {
      id: "returned",
      label: "ລໍຖ້າຜູ້ຂໍແກ້ໄຂ",
      value: (r) => (r.in_flight > 0 ? (r.needs_changes / r.in_flight) * 100 : null),
      text: pct,
      note: "ສັດສ່ວນຂອງລາຍການທີ່ກຳລັງດຳເນີນ ທີ່ຖືກສົ່ງຄືນໃຫ້ແກ້ໄຂ · ສະຖານະຕອນນີ້",
    },
    {
      id: "fees",
      label: "ຄ່າທຳນຽມ",
      value: (r) => r.declared_fee_lak,
      text: moneyText,
      note: "ຄ່າທຳນຽມທີ່ຊຳລະແລ້ວ ນັບຕາມວັນທີ່ຈ່າຍ",
    },
    {
      id: "app",
      label: "ຍື່ນຜ່ານແອັບ",
      value: () => null,
      text: pct,
      note: "ຍັງບໍ່ມີຂໍ້ມູນ — ລະບົບຍັງບໍ່ໄດ້ບັນທຶກຊ່ອງທາງການຍື່ນ (ແອັບ / ເຄົາເຕີ)",
      disabled: true,
    },
  ];
  return finance ? all : all.filter((m) => m.id !== "fees");
}

/** 5 ขั้นสีตามลำดับค่า (quantile) ของพื้นที่ที่มีค่า — ค่าเท่ากันอยู่ขั้นเดียวกัน */
function classOf(values: number[], v: number): number {
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  if (sorted.length <= 1) return v > 0 ? 4 : 0;
  const idx = sorted.indexOf(v);
  return Math.min(4, Math.floor((idx / (sorted.length - 1)) * 4.999));
}

/** ขั้นสี — ผสมจาก token navy กับพื้นขาว (ห้ามใช้สีดิบ) */
const RAMP = [14, 30, 50, 72, 95].map((p) => `color-mix(in srgb, var(--navy-700) ${String(p)}%, var(--surface))`);

interface Unit {
  row: StatsBreakdownRow;
  shape: Shape;
  name: string;
}

export function StatsMap({
  geo,
  mode,
  districtCode,
  rows,
  childWord,
  nameOf,
  today,
  finance,
  onDrill,
}: {
  geo: MapGeo;
  mode: MapMode;
  /** mode district: รหัสเมืองที่กำลังดู */
  districtCode?: string;
  rows: StatsBreakdownRow[];
  childWord: string;
  nameOf: (r: StatsBreakdownRow) => string;
  today: boolean;
  /** สิทธิ์ดูการเงินรวม (DSH-04) */
  finance: boolean;
  onDrill?: (id: string) => void;
}) {
  const ms = metrics(today, finance);
  const [metricId, setMetricId] = useState("requests");
  const [base, setBase] = useState<BaseType>(loadBase);
  const [hover, setHover] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [tileFail, setTileFail] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  const clipId = `gmap-laos-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => {
      if (e) setWidth(Math.max(320, e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function pickBase(b: BaseType) {
    setBase(b);
    setTileFail(false);
    try {
      localStorage.setItem(BASE_KEY, b);
    } catch {
      // เบราว์เซอร์ปิด storage — จำไม่ได้ก็ไม่เป็นไร
    }
  }

  const metric = ms.find((m) => m.id === metricId) ?? ms[0];
  if (!metric) return null;

  const units: Unit[] = [];
  for (const row of rows) {
    if (!row.place_id) continue;
    const shape = shapeOf(geo, mode, row.code, districtCode);
    if (shape) units.push({ row, shape, name: nameOf(row) });
  }
  const unplaced = rows.filter((r) => r.place_id && !units.some((u) => u.row.place_id === r.place_id));

  const active = units.filter((u) => !isIdle(u.row));
  const values = active.map((u) => metric.value(u.row)).filter((v): v is number => v !== null);
  const maxV = values.length ? Math.max(...values) : 0;
  const minV = values.length ? Math.min(...values) : 0;

  // มุมมอง: กรอบของเมืองที่กำลังดู หรือกรอบที่ครอบทุกพื้นที่ลูก + ขอบเล็กน้อย
  const districtShape = districtCode ? geo.districts[districtCode] : undefined;
  // แขวงที่กำลังดู (mode province/district) — รหัสเมือง 2 ตัวแรก = รหัสแขวง
  const provinceCode = mode === "district" ? (districtCode ?? "").slice(0, 2) : mode === "province" ? (units[0]?.row.code ?? "").slice(0, 2) : "";
  const provinceShape = provinceCode ? geo.provinces[provinceCode] : undefined;
  const vb: [number, number, number, number] =
    mode === "district" && districtShape
      ? padBox(districtShape.bb, 0.08)
      : padBox(unionBox(units.map((u) => u.shape.bb)), 0.05);
  const height = Math.round(Math.min(560, Math.max(320, (width * vb[3]) / vb[2])));
  // ขยายมุมมองให้สัดส่วนเท่ากรอบจริง — tile ของแผนที่พื้นหลังจะได้เต็มกรอบ ไม่เหลือขอบเทา
  const [vx, vy, vw, vh] = fitAspect(vb, width / height);
  const fontPx = (vw / width) * 13;

  const focusId = hover ?? picked;
  const focus = focusId ? rows.find((r) => r.place_id === focusId) : undefined;
  // พื้นหลังจาง: เมืองอื่นในแขวงเดียวกัน (mode district) / แขวงอื่น (mode province)
  const context: [string, Shape][] =
    mode === "district"
      ? Object.entries(geo.districts).filter(([k]) => k !== districtCode && k.startsWith(provinceCode))
      : mode === "province"
        ? Object.entries(geo.provinces).filter(([k]) => k !== provinceCode)
        : [];
  const unitCodes = new Set(units.map((u) => u.row.code));
  const tasaengOutlines =
    mode === "province" ? Object.entries(geo.tasaengs).filter(([k]) => unitCodes.has(k.split("/")[0] ?? "")).map(([, v]) => v) : [];
  const approx = mode === "district" || tasaengOutlines.length > 0;

  return (
    <DxCard
      title="ແຜນທີ່ສະຖິຕິ"
      sub={`${String(active.length)} ຈາກ ${String(units.length)} ${childWord}ມີຂໍ້ມູນ · ກົດ${childWord}ເພື່ອເບິ່ງຕົວເລກ`}
      right={
        <span className="gmap-badge" title="ຂອບເຂດເມືອງມາຈາກ geoBoundaries (ຄວາມລະອຽດຫຍາບ ເໝາະສຳລັບສະແດງສະຖິຕິ ບໍ່ແມ່ນຂອບເຂດທາງການ) · ເສັ້ນແບ່ງຕາແສງເປັນການແບ່ງໂດຍປະມານ ເພື່ອສະແດງຕົວເລກເທົ່ານັ້ນ">
          <AlertTriangle size={13} />{" "}
          {approx
            ? "ຂອບເຂດເມືອງ: ຂໍ້ມູນຈິງ · ຂອບເຂດຕາແສງ: ໂດຍປະມານ (ຍັງບໍ່ມີຂໍ້ມູນຈິງ)"
            : "ຂອບເຂດແຂວງ/ເມືອງ: ຂໍ້ມູນຈິງ ປີ 2019 · ບໍ່ແມ່ນຂອບເຂດທາງການ"}
        </span>
      }
    >
      <div className="gmap-bar">
        <div className="gmap-chips" role="group" aria-label="ຕົວຊີ້ວັດ">
          {ms.map((m) => (
            <button
              key={m.id}
              type="button"
              className={m.id === metric.id ? "gmap-chip is-on" : "gmap-chip"}
              aria-pressed={m.id === metric.id}
              title={m.note}
              disabled={m.disabled}
              onClick={() => setMetricId(m.id)}
            >
              {m.label}
              {m.disabled ? <span className="gmap-chip__na">ຍັງບໍ່ມີຂໍ້ມູນ</span> : null}
            </button>
          ))}
        </div>
        <Segmented
          label="ພື້ນຫຼັງແຜນທີ່"
          value={base}
          onChange={pickBase}
          options={[
            { value: "plain", label: "ແຜນຜັງ" },
            { value: "osm", label: "ແຜນທີ່" },
            { value: "sat", label: "ດາວທຽມ" },
          ]}
        />
      </div>

      <div className="gmap">
        <div className="gmap-map" ref={wrapRef}>
          <svg
            viewBox={`${String(vx)} ${String(vy)} ${String(vw)} ${String(vh)}`}
            width="100%"
            height={height}
            role="img"
            aria-label={`ແຜນທີ່${childWord} — ${metric.label}`}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <pattern id="gmap-hatch" width={vw / 120} height={vw / 120} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width={vw / 120} height={vw / 120} fill="var(--surface)" />
                <line x1={0} y1={0} x2={0} y2={vw / 120} stroke="var(--border-strong)" strokeWidth={vw / 400} />
              </pattern>
              {/* แผนที่พื้นหลังแสดงเฉพาะในเขตแดนลาว (ทุกแขวงรวมกัน) นอกประเทศเป็นพื้นเรียบ */}
              <clipPath id={clipId}>
                {Object.entries(geo.provinces).map(([k, s]) => (
                  <path key={k} d={s.d} />
                ))}
              </clipPath>
            </defs>
            <rect x={vx} y={vy} width={vw} height={vh} fill="var(--bg-sunk)" />
            {base !== "plain" && !tileFail ? (
              <g clipPath={`url(#${clipId})`}>
                <Tiles type={base} vb={[vx, vy, vw, vh]} width={width} onFail={() => setTileFail(true)} />
              </g>
            ) : null}
            {provinceCode === "01" ? <path d={geo.river} fill="none" className="gmap-river" strokeWidth={vw / 260} /> : null}
            {context.map(([k, s]) => (
              <path key={k} d={s.d} className="gmap-context" strokeWidth={vw / 900} />
            ))}
            {units.map((u) => {
              const idle = isIdle(u.row);
              const v = metric.value(u.row);
              const fill = idle ? "url(#gmap-hatch)" : v === null ? "var(--bg-sunk)" : RAMP[classOf(values, v)];
              const id = u.row.place_id as string;
              const on = focusId === id;
              return (
                <path
                  key={id}
                  d={u.shape.d}
                  fill={fill}
                  fillOpacity={base === "plain" || tileFail ? 1 : 0.82}
                  className={on ? "gmap-unit is-on" : "gmap-unit"}
                  strokeWidth={on ? vw / 300 : vw / 700}
                  strokeDasharray={mode === "district" ? `${String(vw / 200)} ${String(vw / 400)}` : undefined}
                  onMouseEnter={() => setHover(id)}
                  onClick={() => setPicked(id === picked ? null : id)}
                >
                  <title>{`${u.name}: ${idle ? "ຍັງບໍ່ເປີດໃຊ້" : v === null ? "—" : metric.text(v)}`}</title>
                </path>
              );
            })}
            {tasaengOutlines.map((s, i) => (
              <path
                key={i}
                d={s.d}
                fill="none"
                className="gmap-approx"
                strokeWidth={vw / 1400}
                strokeDasharray={`${String(vw / 300)} ${String(vw / 500)}`}
                pointerEvents="none"
              />
            ))}
            {districtShape && mode === "district" ? (
              <path d={districtShape.d} fill="none" className="gmap-outline" strokeWidth={vw / 260} pointerEvents="none" />
            ) : provinceShape ? (
              <path d={provinceShape.d} fill="none" className="gmap-outline" strokeWidth={vw / 450} pointerEvents="none" />
            ) : null}
            {units.map((u) => {
              const idle = isIdle(u.row);
              const v = metric.value(u.row);
              const dark = !idle && v !== null && classOf(values, v) >= 3;
              return (
                <g key={`l-${u.row.place_id ?? ""}`} pointerEvents="none" className={dark ? "gmap-lab is-dark" : "gmap-lab"}>
                  <text x={u.shape.c[0]} y={u.shape.c[1] - fontPx * 0.3} fontSize={fontPx} textAnchor="middle" fontWeight={700}>
                    {u.name}
                  </text>
                  <text x={u.shape.c[0]} y={u.shape.c[1] + fontPx * 1.1} fontSize={fontPx * 0.95} textAnchor="middle" className="num">
                    {idle ? "ຍັງບໍ່ເປີດໃຊ້" : v === null ? "—" : metric.text(v)}
                  </text>
                </g>
              );
            })}
          </svg>
          {tileFail ? (
            <p className="gmap-tilefail">ຮູບແຜນທີ່ພື້ນຖານໂຫຼດບໍ່ໄດ້ (ບໍ່ມີອິນເຕີເນັດ ຫຼື ຖືກບລັອກ) — ສະແດງສະເພາະຂອບເຂດ</p>
          ) : null}
        </div>

        <aside className="gmap-side" aria-label="ຕົວເລກຂອງຂອບເຂດທີ່ເລືອກ">
          {focus ? (
            <SidePanel row={focus} name={nameOf(focus)} ms={ms} metricId={metric.id} onMetric={setMetricId} onDrill={onDrill} childWord={childWord} />
          ) : (
            <p className="gmap-hint">ຊີ້ ຫຼື ກົດ{childWord}ເທິງແຜນທີ່ ເພື່ອເບິ່ງຕົວເລກ ແລະ ເຂົ້າໄປເບິ່ງ Dashboard ຂອງ{childWord}ນັ້ນ</p>
          )}
          <div className="gmap-legend">
            <div className="gmap-legend__ramp">
              <span className="num">{values.length ? metric.text(minV) : "—"}</span>
              {RAMP.map((c) => (
                <i key={c} style={{ background: c }} />
              ))}
              <span className="num">{values.length ? metric.text(maxV) : "—"}</span>
            </div>
            <p>ໜ້ອຍ → ຫຼາຍ · ແບ່ງ 5 ກຸ່ມ ຕາມລຳດັບຄ່າ</p>
            <p>
              <i className="gmap-legend__hatch" /> ຍັງບໍ່ເປີດໃຊ້ (ບໍ່ມີເຈົ້າໜ້າທີ່ປະຈຳ ແລະ ບໍ່ມີຄຳຮ້ອງ)
            </p>
            <p>
              <i className="gmap-legend__line" /> {mode === "country" ? "ຂອບແຂວງ" : "ຂອບເມືອງ"} (ຂໍ້ມູນຈິງ)
              {approx ? (
                <>
                  {" "}· <i className="gmap-legend__line is-dash" /> ຂອບຕາແສງ (ໂດຍປະມານ)
                </>
              ) : null}
            </p>
            <p className="gmap-legend__note">{metric.note}</p>
            {unplaced.length > 0 ? (
              <p className="gmap-legend__note">
                ບໍ່ມີຮູບຮ່າງເທິງແຜນທີ່: {unplaced.map(nameOf).join(", ")} — ເບິ່ງຕົວເລກໃນຕາຕະລາງລຸ່ມ
              </p>
            ) : null}
          </div>
        </aside>
      </div>
      <p className="gmap-attr">
        {geo.attribution}
        {base !== "plain" && !tileFail ? ` · ${TILES[base].attr}` : ""}
      </p>
    </DxCard>
  );
}

function SidePanel({
  row,
  name,
  ms,
  metricId,
  onMetric,
  onDrill,
  childWord,
}: {
  row: StatsBreakdownRow;
  name: string;
  ms: Metric[];
  metricId: string;
  onMetric: (id: string) => void;
  onDrill?: (id: string) => void;
  childWord: string;
}) {
  const idle = isIdle(row);
  return (
    <div className="gmap-panel">
      <h3 className="gmap-panel__name">{name}</h3>
      <p className="gmap-panel__sub">
        {row.tasaengs > 0 ? (
          <span className={row.tasaengs_staffed > 0 ? "status-badge status-badge--success" : "status-badge"}>
            ເປີດໃຊ້ແລ້ວ <span className="num">{`${String(row.tasaengs_staffed)}/${String(row.tasaengs)}`}</span> ຕາແສງ
          </span>
        ) : null}
        {row.villages > 0 ? <span className="num"> · {num(row.villages)} ບ້ານ</span> : null}
      </p>
      {idle ? (
        <p className="gmap-nd">
          <strong>ຍັງບໍ່ມີຂໍ້ມູນ</strong> ຄຳຮ້ອງ ແລະ ຄ່າທຳນຽມຂອງ{childWord}ນີ້ (ບໍ່ແມ່ນ 0)
        </p>
      ) : (
        <div className="gmap-tiles">
          {ms.map((m) => {
            const v = m.value(row);
            return (
              <button
                key={m.id}
                type="button"
                className={m.id === metricId ? "gmap-tile is-on" : "gmap-tile"}
                aria-pressed={m.id === metricId}
                disabled={m.disabled}
                title={m.note}
                onClick={() => onMetric(m.id)}
              >
                <span className="gmap-tile__l">{m.label}</span>
                <span className="gmap-tile__v num">{m.disabled ? "—" : v === null ? "—" : m.text(v)}</span>
                {m.id === "overdue" && row.in_flight > 0 ? (
                  <span className="gmap-tile__s num">
                    {num(row.overdue_over_7d)} ຈາກ {num(row.in_flight)}
                  </span>
                ) : m.id === "issued" && row.avg_processing_seconds > 0 ? (
                  <span className="gmap-tile__s">ສະເລ່ຍ {duration(row.avg_processing_seconds)}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      {onDrill && row.place_id ? (
        <button type="button" className="btn-primary gmap-drill" onClick={() => onDrill(row.place_id as string)}>
          ເບິ່ງ Dashboard {name} <ArrowRight size={14} />
        </button>
      ) : null}
    </div>
  );
}

function fitAspect(vb: [number, number, number, number], aspect: number): [number, number, number, number] {
  const [x, y, w, h] = vb;
  if (w / h < aspect) {
    const nw = h * aspect;
    return [x - (nw - w) / 2, y, nw, h];
  }
  const nh = w / aspect;
  return [x, y - (nh - h) / 2, w, nh];
}

function unionBox(bbs: [number, number, number, number][]): [number, number, number, number] {
  if (bbs.length === 0) return [0, 0, 1, 1];
  return [
    Math.min(...bbs.map((b) => b[0])),
    Math.min(...bbs.map((b) => b[1])),
    Math.max(...bbs.map((b) => b[2])),
    Math.max(...bbs.map((b) => b[3])),
  ];
}

function padBox(bb: [number, number, number, number], pad: number): [number, number, number, number] {
  const w = bb[2] - bb[0];
  const h = bb[3] - bb[1];
  const p = Math.max(w, h) * pad;
  return [bb[0] - p, bb[1] - p, w + 2 * p, h + 2 * p];
}

/** tile ของ OSM/Esri วางในพิกัดเดียวกับรูปร่าง (พิกเซลโลก z12) — ขนาดบนจอราว 256px */
function Tiles({
  type,
  vb,
  width,
  onFail,
}: {
  type: Exclude<BaseType, "plain">;
  vb: [number, number, number, number];
  width: number;
  onFail: () => void;
}) {
  const failed = useRef(0);
  const [vx, vy, vw, vh] = vb;
  const z = Math.max(5, Math.min(TILES[type].max, Math.ceil(GEO_ZOOM + Math.log2(width / vw))));
  const size = 256 * Math.pow(2, GEO_ZOOM - z);
  const n = Math.pow(2, z);
  const tiles: { x: number; y: number }[] = [];
  for (let x = Math.floor(vx / size); x <= Math.floor((vx + vw) / size); x++) {
    for (let y = Math.floor(vy / size); y <= Math.floor((vy + vh) / size); y++) {
      if (x >= 0 && y >= 0 && x < n && y < n) tiles.push({ x, y });
    }
  }
  const url = (x: number, y: number) =>
    TILES[type].url.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y));
  return (
    <g pointerEvents="none" aria-hidden="true">
      {tiles.map(({ x, y }) => (
        <image
          key={`${type}-${String(z)}-${String(x)}-${String(y)}`}
          href={url(x, y)}
          x={x * size}
          y={y * size}
          // ซ้อนกันเล็กน้อยกันเส้นขาวระหว่าง tile
          width={size * 1.002}
          height={size * 1.002}
          preserveAspectRatio="none"
          onError={() => {
            failed.current += 1;
            if (failed.current >= Math.min(3, tiles.length)) onFail();
          }}
        />
      ))}
    </g>
  );
}
